import {
  AbstractType,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  InjectionToken,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Type,
} from '@angular/core';
import { Router } from '@angular/router';
import { lazyInject } from '@app/containers/base/decorators';
import {
  EventsService,
  StandWorksheetService,
  StateService,
  XprtDataService,
} from '@app/shared/services';
import {
  ColumnFilterModel,
  ENTITY_TYPES,
  FilterModel,
  IBaseEntityType,
  IEvent,
  IEventStatus,
  IFieldPreferences,
  IProcessedEvent,
} from '@app/shared/types';
import {
  STAND_WORKSHEET_STATUS_TOTAL_VALUE,
  STAND_WORKSHEET_STATUS_VALUE,
} from '@app/shared/types/enums/stand-worksheet-status.enum';
import { EventsActions, UiActions } from '@app/store/actions';
import {
  cloneDeep,
  filter,
  find,
  forEach,
  get,
  head,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  map,
  set,
  size,
  split,
} from 'lodash';
import { withDndGlobalLoader } from '../xux-dnd/decorators/xux-dnd-loader.decorators';
import { rx } from '@app/shared/rx';
import { IProgressBarItem } from '../xux/xux-progress-bar/xux-progress-bar.component.type';
import { IStandWorksheetSummaryFilter } from '@app/shared/types/common/stand-worksheet-summary.type';
import { XVII18nService } from '@app/i18n/services';
import { I18n } from '@app/i18n/strings-keys';
import { DashboardWidgetBase } from '@components/dashboard-widget-base/dashboard-widget-base';
import { EVENT_SELECTOR_SELECT } from '@config/global-constants';

interface IStandWorksheetInfo {
  count?: number;
  text?: string;
  status?: string;
  isHovered?: boolean;
}

@Component({
  selector: 'stand-worksheet-summary',
  templateUrl: 'stand-worksheet-summary.component.html',
  styleUrls: ['./stand-worksheet-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StandWorksheetSummaryComponent
  extends DashboardWidgetBase
  implements OnInit, OnDestroy
{
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(Router) public router: Router;
  @lazyInject(StandWorksheetService)
  public standWorksheetService: StandWorksheetService;
  @lazyInject(EventsActions) public eventsActions: EventsActions;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(XVII18nService) public i18nService: XVII18nService;
  @lazyInject(EventsService) public eventsService: EventsService;

  @Input() public field: IFieldPreferences = {};

  public defaultSummaryList: IStandWorksheetInfo[] = [];

  public summaryList: IStandWorksheetInfo[] = [];

  public defaultProgressBarData: IProgressBarItem[] = [];
  public progressBarData: IProgressBarItem[] = [];
  public NoDataText: string = I18n.InfoMessages.NoStandWorksheet;
  public selectedEvent: IProcessedEvent;

  public constructor(
    public injector: Injector,
    private cdr: ChangeDetectorRef,
    protected stateService: StateService,
  ) {
    super(stateService);
  }

  public ngOnInit(): void {
    this.initListConfig();
    super.ngOnInit();
    const change = this.field.control?.root.valueChanges
      .pipe(rx.debounceTime(100))
      .subscribe(
        (standWorksheetSummaryFilter: IStandWorksheetSummaryFilter) => {
          this.handleDataUpdate(standWorksheetSummaryFilter);
        },
      );
    this.subscriptions.push(change);
  }

  public async handleDataUpdate(
    standWorksheetSummaryFilter: IStandWorksheetSummaryFilter,
  ): Promise<void> {
    const { eventId } = standWorksheetSummaryFilter;
    if (!eventId) {
      return;
    }

    const events = await this.fetchEventData(eventId);

    this.selectedEvent = isEmpty(eventId) ? null : head(events);
    this.widgetQueryFilter = cloneDeep(standWorksheetSummaryFilter);
    const eventStatus = get(this.selectedEvent, 'status', '');

    if (!includes([IEventStatus.Closed, IEventStatus.Open], eventStatus)) {
      this.clearStandWorksheetData();

      return;
    }
    if (!isEmpty(eventId) && !isEmpty(this.selectedEvent)) {
      const excludeStandWorksheetStatus =
        eventStatus && isEqual(eventStatus, IEventStatus.Open)
          ? [STAND_WORKSHEET_STATUS_VALUE.Completed]
          : [
              STAND_WORKSHEET_STATUS_VALUE.Open,
              STAND_WORKSHEET_STATUS_VALUE.Approved,
            ];
      this.summaryList = cloneDeep(
        filter(this.defaultSummaryList, (item) => {
          return (
            excludeStandWorksheetStatus.indexOf(
              item.status as STAND_WORKSHEET_STATUS_VALUE,
            ) === -1
          );
        }),
      );
      this.progressBarData = cloneDeep(
        filter(
          this.defaultProgressBarData,
          (item) => !includes(excludeStandWorksheetStatus, item.status),
        ),
      );
      await this.getStandWorksheetsData(eventId);
    }
  }

  public clearStandWorksheetData(): void {
    this.summaryList = [];
    this.progressBarData = [];
    const eventControl = this.field.control?.root.get('eventId');
    this.stopFetchInterval();
    this.field.widgetData.autoRefresh.autoRefreshObs.next(false);
    eventControl.patchValue(null);
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public initListConfig(): void {
    this.defaultSummaryList = [
      {
        status: STAND_WORKSHEET_STATUS_TOTAL_VALUE.Total,
        text: this.i18nService.instant(I18n.Common.Total),
        count: 0,
      },
      {
        status: STAND_WORKSHEET_STATUS_VALUE.Open,
        text: this.i18nService.instant(I18n.Status.Open),
        count: 0,
      },
      {
        status: STAND_WORKSHEET_STATUS_VALUE.Approved,
        text: this.i18nService.instant(I18n.Status.Approved),
        count: 0,
      },
      {
        status: STAND_WORKSHEET_STATUS_VALUE.Completed,
        text: this.i18nService.instant(I18n.Status.Completed),
        count: 0,
      },
      {
        status: STAND_WORKSHEET_STATUS_VALUE.Canceled,
        text: this.i18nService.instant(I18n.Status.Canceled),
        count: 0,
      },
    ];
    this.defaultProgressBarData = [
      {
        label: this.i18nService.instant(I18n.Status.Open),
        value: 0,
        color: 'var(--widget-progress-bar-open-color)',
        status: STAND_WORKSHEET_STATUS_VALUE.Open,
      },
      {
        label: this.i18nService.instant(I18n.Status.Approved),
        value: 0,
        color: 'var(--widget-progress-bar-approved-color)',
        status: STAND_WORKSHEET_STATUS_VALUE.Approved,
      },
      {
        label: this.i18nService.instant(I18n.Status.Completed),
        value: 0,
        color: 'var(--widget-progress-bar-completed-color)',
        status: STAND_WORKSHEET_STATUS_VALUE.Completed,
      },
      {
        label: this.i18nService.instant(I18n.Status.Canceled),
        value: 0,
        color: `var(--widget-progress-bar-canceled-color)`,
        status: STAND_WORKSHEET_STATUS_VALUE.Canceled,
      },
    ];
  }

  public async gotoStandWorksheetPage(
    status: STAND_WORKSHEET_STATUS_VALUE | STAND_WORKSHEET_STATUS_TOTAL_VALUE,
  ): Promise<void> {
    this.uiActions.enableLoader();
    const events = await this.fetchEventData([get(this.selectedEvent, 'id')]);
    this.uiActions.disableLoader();
    if (isEmpty(events)) {
      this.uiActions.showErrorToasterMessage(
        I18n.Components.LandingPageComponent.SelectedEventDeleted,
      );
      this.clearStandWorksheetData();

      return;
    }

    if (head(events).status === IEventStatus.Pending) {
      this.uiActions.showErrorToasterMessage(
        I18n.Components.LandingPageComponent.SelectedEventPending,
      );
      this.clearStandWorksheetData();

      return;
    }

    this.updateStandWorksheetListViewData(status);
    this.router.navigateByUrl(this.standWorksheetService.config.uiRoute);
  }

  private updateStandWorksheetListViewData(
    status: STAND_WORKSHEET_STATUS_VALUE | STAND_WORKSHEET_STATUS_TOTAL_VALUE,
  ): void {
    const filterModel: FilterModel = {};
    set(filterModel, 'status', this.prepareStatus(status));
    const processEventsList = this.xprtDataService.processEventsList([
      this.selectedEvent as IEvent,
    ]);
    this.eventsActions.saveSelectedEvents(processEventsList);
    this.uiActions.restoreDefaultListView(ENTITY_TYPES.STAND_WORKSHEET);
    this.uiActions.updateListViewData({
      filterModel,
      entityType: ENTITY_TYPES.STAND_WORKSHEET,
    });
  }

  public prepareStatus(
    status: STAND_WORKSHEET_STATUS_VALUE | STAND_WORKSHEET_STATUS_TOTAL_VALUE,
  ): ColumnFilterModel {
    if (!isEqual(status, STAND_WORKSHEET_STATUS_TOTAL_VALUE.Total)) {
      return {
        type: 'containsAny',
        filter: [status],
        headerText: status,
      };
    }

    return null;
  }

  public async fetchEventData(eventId: string[]): Promise<IProcessedEvent[]> {
    const params = {
      filter: `_id eq in(${eventId.join()})`,
      select: split(EVENT_SELECTOR_SELECT),
    };
    const events = (await this.eventsService
      .fetchList(params)
      .pipe(
        rx.first(),
        rx.map((res) => get(res, 'items')),
      )
      .toPromise()) as IProcessedEvent[];

    return events;
  }

  @withDndGlobalLoader('stand_worksheet')
  public async getStandWorksheetsData(eventId: string[]): Promise<void> {
    const queryFilter: string = `event_id eq objectid(${eventId.toString()}) and is_active eq true`;

    const standWorksheets: IBaseEntityType[] = await this.standWorksheetService
      .fetchList(
        {
          select: ['status'],
          filter: `${queryFilter}`,
        },
        {
          noLoader: true,
          showErrorMessage: true,
        },
      )
      .pipe(rx.map((res) => res.items))
      .toPromise();

    this.transformStandWorksheetData(standWorksheets as IStandWorksheetInfo[]);
    this.cdr.detectChanges();
  }

  private transformStandWorksheetData(
    standWorksheets: IStandWorksheetInfo[],
  ): void {
    const standWorksheetsCount = size(standWorksheets);
    forEach(standWorksheets, (standWorksheet) => {
      forEach(this.summaryList, (summary) => {
        if (summary.status === STAND_WORKSHEET_STATUS_TOTAL_VALUE.Total) {
          summary.count = standWorksheetsCount;
        }

        if (summary.status === standWorksheet.status) {
          summary.count += 1;
        }
      });
    });

    this.prepareProgressBarData();
  }

  public prepareProgressBarData(): void {
    this.progressBarData = map(
      this.progressBarData,
      (progressBarItem: IProgressBarItem) => {
        const target = find(
          this.summaryList,
          (item) => progressBarItem.label === item.text,
        );

        return {
          ...progressBarItem,
          value: get(target, 'count', 0),
        };
      },
    );
  }

  public handleHover(hoveredData: IProgressBarItem): void {
    forEach(this.summaryList, (item: IStandWorksheetInfo) => {
      item.isHovered = hoveredData?.label === item.text;
    });
  }
}
